import { useMemo, useState } from 'react';

// material-ui
import {
    FormControl,
    MenuItem,
    OutlinedInput,
    OutlinedInputProps,
    Select,
    Slider,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import { useAsyncDebounce, Row, TableState, MetaBase, FilterProps } from 'react-table';
// import { matchSorter } from 'match-sorter';
import { format } from 'date-fns';

// project import
import IconButton from 'components/@extended/IconButton';

// assets
import { CloseOutlined, LineOutlined, SearchOutlined } from '@ant-design/icons';

interface GlobalFilterProps extends OutlinedInputProps {
    preGlobalFilteredRows: Row<{}>[];
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
}

export function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter, ...other }: GlobalFilterProps) {
    const count = preGlobalFilteredRows.length;
    const [value, setValue] = useState(globalFilter);
    const onChange = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 200);

    return (
        <OutlinedInput
            value={value || ''}
            onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
            }}
            placeholder={`Search ${count} records...`}
            startAdornment={<SearchOutlined />}
            {...other}
        />
    );
}

export function DefaultColumnFilter({ column: { filterValue, Header, setFilter } }: any) {
    const [value, setValue] = useState(filterValue || '');
    const onChange = useAsyncDebounce((value) => {
        setFilter(value || undefined);
    }, 200);
    return (
        <TextField
            fullWidth
            value={value}
            onChange={(e) => {
                setValue(e.target.value || '');
                onChange(e.target.value || '');
            }}
            placeholder={Header}
            size="small"
        />
    );
}

export function DateColumnFilter({ column: { filterValue, setFilter } }: any) {
    return (
        <FormControl sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    format="dd-MM-yyyy"
                    value={filterValue ? new Date(filterValue) : null}
                    onChange={(newValue) => {
                        let formatDateFn = undefined;
                        try {
                            formatDateFn = newValue ? format(newValue, 'yyyy-MM-dd') : undefined;
                        } catch (error) {}
                        setFilter(formatDateFn);
                    }}
                />
            </LocalizationProvider>
        </FormControl>
    );
}

interface FilterColumnProps {
    filterValue?: never[];
    preFilteredRows: Row[];
    Header: string;
    setFilter: any;
    id: string;
}

export type SelectOptionType = { label: string; value: string | number };

export type SelectColumnFilterProps = FilterProps<{}> & {
    options: SelectOptionType[];
};

export function SelectColumnFilter({ column: { filterValue, setFilter }, options }: SelectColumnFilterProps) {
    // Check if filterValue contains multiple values separated by semicolon
    const isMultiValue = filterValue && typeof filterValue === 'string' && filterValue.includes(';');

    // If it's a multi-value, check if it matches all available option values
    let displayValue = filterValue || '';
    if (isMultiValue) {
        // Split the filter value and sort it
        const filterValues = filterValue
            .split(';')
            .sort((a, b) => a.localeCompare(b))
            .join(';');
        // Get all option values and sort them
        const allOptionValues = options
            .map((opt) => String(opt.value))
            .sort((a, b) => a.localeCompare(b))
            .join(';');

        // If filter matches all options, treat it as "All"
        if (filterValues === allOptionValues) {
            displayValue = '';
        }
    }

    return (
        <Select
            value={displayValue}
            onChange={(e) => {
                setFilter(e.target.value || undefined);
            }}
            displayEmpty
            size="small"
        >
            <MenuItem value="">All</MenuItem>
            {options.map((option: SelectOptionType, i: number) => (
                <MenuItem key={i} value={option.value === 0 ? option.value.toString() : option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Select>
    );
}

export function SliderColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}: {
    column: FilterColumnProps;
}) {
    const [min, max] = useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        preFilteredRows.forEach((row: Row) => {
            min = Math.min(row.values[id], min);
            max = Math.max(row.values[id], max);
        });
        return [min, max];
    }, [id, preFilteredRows]);

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, minWidth: 120 }}>
            <Slider
                value={filterValue || min}
                min={min}
                max={max}
                step={1}
                onChange={(event: Event, newValue: number | number[]) => {
                    setFilter(newValue);
                }}
                valueLabelDisplay="auto"
                aria-labelledby="non-linear-slider"
            />
            <Tooltip title="Reset">
                <IconButton size="small" color="error" onClick={() => setFilter(undefined)}>
                    <CloseOutlined />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export function NumberRangeColumnFilter({
    column: { filterValue = [], preFilteredRows, setFilter, id },
}: {
    column: FilterColumnProps;
}) {
    const [min, max] = useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        preFilteredRows.forEach((row: Row) => {
            min = Math.min(row.values[id], min);
            max = Math.max(row.values[id], max);
        });
        return [min, max];
    }, [id, preFilteredRows]);

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 168, maxWidth: 250 }}>
            <TextField
                fullWidth
                value={filterValue[0] || ''}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setFilter((old = []) => [val ? parseInt(val, 10) : undefined, old[1]]);
                }}
                placeholder={`Min (${min})`}
                size="small"
            />
            <LineOutlined />
            <TextField
                fullWidth
                value={filterValue[1] || ''}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setFilter((old = []) => [old[0], val ? parseInt(val, 10) : undefined]);
                }}
                placeholder={`Max (${max})`}
                size="small"
            />
        </Stack>
    );
}

export function filterGreaterThan(rows: Row[], id: number, filterValue: number) {
    return rows.filter((row: Row) => {
        const rowValue = row.values[id];
        return rowValue >= filterValue;
    });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val: any) => typeof val !== 'number';

export function useControlledState(state: TableState<{}>, { instance }: MetaBase<{}>) {
    return useMemo(() => {
        if (state.groupBy.length) {
            return {
                ...state,
                hiddenColumns: [...state.hiddenColumns!, ...state.groupBy].filter((d, i, all) => all.indexOf(d) === i),
            };
        }
        return state;
    }, [state]);
}

export function roundedMedian(leafValues: any) {
    let min = leafValues[0] || 0;
    let max = leafValues[0] || 0;

    leafValues.forEach((value: number) => {
        min = Math.min(min, value);
        max = Math.max(max, value);
    });

    return Math.round((min + max) / 2);
}
